import axiosHttp from "services/axiosHttp";
const { createSlice, createAsyncThunk } = require("@reduxjs/toolkit");

// IMPORTANT: there is no dedicated "list all active allocations" endpoint
// in the backend (only GET /:id/allocations per-asset and
// GET /allocations/user/:userId per-user exist). So both tables on this page
// reuse the main GET /assets endpoint with a status filter:
//   - "Available Assets" table  -> GET /assets?status=AVAILABLE
//   - "Active Allocations" table -> GET /assets?status=ALLOCATED
//
// NOTE: the asset list endpoint's select does NOT include department, or the
// allocated user's name (only allocations[0].user_id) — flag this to backend
// if you need those columns populated with more than a raw ID.

export const fetchAvailableAssets = createAsyncThunk("fetchAvailableAssets", async ({ page = 1, limit = 100 }) => {
	const params = new URLSearchParams({ page, size: limit, status: "AVAILABLE" });
	const response = await axiosHttp.get(`assets?${params}`);
	const data = response.data?.data ?? response.data;
	return { docs: data.content ?? [], total: data.total };
});

export const fetchActiveAllocations = createAsyncThunk("fetchActiveAllocations", async ({ page = 1, limit = 100 }) => {
	const params = new URLSearchParams({ page, size: limit, status: "ALLOCATED" });
	const response = await axiosHttp.get(`assets?${params}`);
	const data = response.data?.data ?? response.data;
	return { docs: data.content ?? [], total: data.total };
});

// Fetch the current allocation record for one asset — needed to get the
// allocationId before opening the Return modal (the asset-list response
// only gives us the user_id, not the allocation's own id).
export const fetchCurrentAllocationForAsset = createAsyncThunk(
	"fetchCurrentAllocationForAsset",
	async ({ assetId }) => {
		const response = await axiosHttp.get(`assets/${assetId}/allocations/current`);
		return response.data?.data ?? response.data;
	}
);

const AllocationListSlice = createSlice({
	name: "allocationListSlice",
	initialState: {
		availableAssets: { isLoading: false, docs: [], total: 0, error: null },
		activeAllocations: { isLoading: false, docs: [], total: 0, error: null },
		currentAllocation: { isLoading: false, data: null, error: null },
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAvailableAssets.pending, (state) => {
				state.availableAssets.isLoading = true;
			})
			.addCase(fetchAvailableAssets.fulfilled, (state, action) => {
				state.availableAssets.isLoading = false;
				state.availableAssets.docs = action.payload.docs;
				state.availableAssets.total = action.payload.total;
			})
			.addCase(fetchAvailableAssets.rejected, (state, action) => {
				state.availableAssets.isLoading = false;
				state.availableAssets.error = action.payload;
			})

			.addCase(fetchActiveAllocations.pending, (state) => {
				state.activeAllocations.isLoading = true;
			})
			.addCase(fetchActiveAllocations.fulfilled, (state, action) => {
				state.activeAllocations.isLoading = false;
				state.activeAllocations.docs = action.payload.docs;
				state.activeAllocations.total = action.payload.total;
			})
			.addCase(fetchActiveAllocations.rejected, (state, action) => {
				state.activeAllocations.isLoading = false;
				state.activeAllocations.error = action.payload;
			})

			.addCase(fetchCurrentAllocationForAsset.pending, (state) => {
				state.currentAllocation.isLoading = true;
			})
			.addCase(fetchCurrentAllocationForAsset.fulfilled, (state, action) => {
				state.currentAllocation.isLoading = false;
				state.currentAllocation.data = action.payload;
			})
			.addCase(fetchCurrentAllocationForAsset.rejected, (state, action) => {
				state.currentAllocation.isLoading = false;
				state.currentAllocation.error = action.payload;
			});
	},
});

export default AllocationListSlice.reducer;
